import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'

import type {
    ShopPostAction,
    ShopPostCategoryInput,
    ShopPostInput,
    ShopPostListParams,
} from '@/lib/api/shop-cms-api'
import { shopCmsApi } from '@/lib/api/shop-cms-api'

export const shopPostKeys = {
    categories: ['shop-post-categories'] as const,
    lists: ['shop-posts'] as const,
    list: (p: ShopPostListParams) => ['shop-posts', p] as const,
    detail: (id: string) => ['shop-post', id] as const,
}

export function useShopPostCategories() {
    return useQuery({
        queryKey: shopPostKeys.categories,
        queryFn: () => shopCmsApi.postCategories(),
    })
}

export function useSaveShopPostCategory() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            input,
        }: {
            id?: string
            input: ShopPostCategoryInput
        }) =>
            id
                ? shopCmsApi.updatePostCategory(id, input)
                : shopCmsApi.createPostCategory(input),
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: shopPostKeys.categories }),
    })
}

export function useDeleteShopPostCategory() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => shopCmsApi.deletePostCategory(id),
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: shopPostKeys.categories }),
    })
}

export function useShopPosts(params: ShopPostListParams) {
    return useQuery({
        queryKey: shopPostKeys.list(params),
        queryFn: () => shopCmsApi.posts(params),
        placeholderData: keepPreviousData,
    })
}

export function useShopPost(id: string | undefined) {
    return useQuery({
        queryKey: shopPostKeys.detail(id ?? ''),
        queryFn: () => shopCmsApi.post(id!),
        enabled: !!id,
    })
}

export type ShopPostSave =
    | { kind: 'create'; input: ShopPostInput }
    | { kind: 'save'; id: string; input: ShopPostInput }
    | { kind: ShopPostAction; id: string; input: ShopPostInput }

/** Mọi thao tác ghi bài (tạo, lưu nháp, đăng, gỡ, bỏ nháp): cập nhật cache bài vừa lưu. */
export function useSaveShopPost() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (s: ShopPostSave) => {
            switch (s.kind) {
                case 'create':
                    return shopCmsApi.createPost(s.input)
                case 'save':
                    return shopCmsApi.savePost(s.id, s.input)
                default:
                    return shopCmsApi.postAction(s.id, s.kind, s.input)
            }
        },
        onSuccess: (res) => {
            const saved = res.data
            if (saved) qc.setQueryData(shopPostKeys.detail(saved.id), res)
            void qc.invalidateQueries({ queryKey: shopPostKeys.lists })
            void qc.invalidateQueries({ queryKey: shopPostKeys.categories })
        },
    })
}

export function useDeleteShopPost() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => shopCmsApi.deletePost(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: shopPostKeys.lists }),
    })
}
